/**
 * "Hours each day" section of the event create/edit forms (migration 2026-10-27-1120).
 *
 * Shown only when the event runs over more than one day. Off by default: the operator turns on "Set hours for each
 * day" and gets one row per date, pre-filled from the event's start and end. Day 1's opening is the event start time
 * and the last day's closing is the event end time (read-only here, change them above); every other time can be
 * changed. When the dates change, the rows follow (new dates get the starting hours, dates no longer in the event
 * drop off) and the section says so.
 *
 * Controlled: the parent keeps `value` and saves it with planDayHoursSave() after the event itself is saved.
 */
import { useMemo } from "react";
import {
  DAILY_HOURS_REASON_TEXT,
  formatClock,
  formatDayLabel,
  reconcileDayHours,
  validateDayHours,
  type DailyHoursApplicability,
  type DayHours,
} from "../../utils/eventDayHours";
import { timeSelectOptions } from "./DateRangePicker";

export interface DailyHoursValue {
  enabled: boolean;
  /** Operator's hours by date (may include dates no longer in the event; reconciled on render and on save). */
  days: DayHours[];
  /**
   * True only once the value was built from a SUCCESSFUL read of the saved hours (or, on create, from "none yet").
   * An uninitialized value never writes anything (external audit 705a4bd9 F1: a draft opened before the read
   * finished looked like "turned off" and the save deleted the saved hours).
   */
  initialized: boolean;
  /** The operator changed the section (toggle or a time). Only a touched, initialized value can clear hours. */
  touched: boolean;
}

/** Not loaded yet: the section is disabled and a save leaves the hours alone. */
export const UNLOADED_DAILY_HOURS: DailyHoursValue = {
  enabled: false,
  days: [],
  initialized: false,
  touched: false,
};

/** Loaded, nothing saved (also the starting value for a NEW event). */
export const EMPTY_DAILY_HOURS: DailyHoursValue = {
  enabled: false,
  days: [],
  initialized: true,
  touched: false,
};

/**
 * Value to start the form with. `saved` must be the result of a SUCCESSFUL read: `undefined`/`null` means "not loaded"
 * (pending or failed) and gives an uninitialized value; `[]` means "loaded, none saved".
 */
export function initialDailyHoursValue(
  saved: DayHours[] | null | undefined,
): DailyHoursValue {
  if (!saved) {
    return UNLOADED_DAILY_HOURS;
  }
  return saved.length > 0
    ? { enabled: true, days: saved, initialized: true, touched: false }
    : EMPTY_DAILY_HOURS;
}

export type DayHoursSavePlan =
  | { action: "none" }
  | { action: "set"; days: DayHours[] }
  | { action: "clear" }
  | { action: "invalid"; message: string };

/**
 * What saving the form must do with the daily hours. Nothing is ever written from a value that was not initialized
 * from a successful read, or when the saved set is unknown.
 *   - enabled and the event runs over 2..14 timed days -> set the reconciled days (refuse when a day is blank or
 *     reversed); nothing when that equals what is saved;
 *   - CLEAR only when the operator explicitly turned the section off (initialized, touched, enabled false) and hours
 *     are saved;
 *   - anything else -> nothing. In particular an event that became single-day / all-day / date TBD KEEPS its saved
 *     hours (the display ignores them while they do not apply); nothing is removed implicitly.
 */
export function planDayHoursSave(
  app: DailyHoursApplicability,
  value: DailyHoursValue,
  saved: DayHours[] | null | undefined,
): DayHoursSavePlan {
  if (!value.initialized || !saved) {
    return { action: "none" };
  }
  if (value.enabled && app.ok) {
    const { days } = reconcileDayHours(app, value.days);
    const errors = validateDayHours(days);
    const firstBad = days.find((d) => errors[d.service_date]);
    if (firstBad) {
      return {
        action: "invalid",
        message: `Hours each day: ${formatDayLabel(firstBad.service_date)} - ${errors[firstBad.service_date]}`,
      };
    }
    const same =
      saved.length === days.length &&
      days.every((d) => {
        const s = saved.find((x) => x.service_date === d.service_date);
        return (
          !!s &&
          s.opens_at.slice(0, 5) === d.opens_at &&
          s.closes_at.slice(0, 5) === d.closes_at
        );
      });
    return same ? { action: "none" } : { action: "set", days };
  }
  if (!value.enabled && value.touched && saved.length > 0) {
    return { action: "clear" };
  }
  return { action: "none" };
}

export default function EventDailyHoursEditor({
  app,
  value,
  onChange,
  saved,
  readOnlyReason,
  loadError,
}: {
  app: DailyHoursApplicability;
  value: DailyHoursValue;
  onChange: (v: DailyHoursValue) => void;
  /** Rows currently saved for the event ([] on create; undefined while not loaded). */
  saved: DayHours[] | null | undefined;
  /** When set, the section is shown read-only with this explanation. */
  readOnlyReason?: string | null;
  /** The saved hours could not be read: shown, and the section stays disabled (a save leaves them alone). */
  loadError?: string | null;
}) {
  const savedCount = saved?.length ?? 0;
  const reconciled = useMemo(
    () => (app.ok ? reconcileDayHours(app, value.days) : null),
    [app, value.days],
  );
  const errors = useMemo(
    () => (reconciled ? validateDayHours(reconciled.days) : {}),
    [reconciled],
  );

  // Not applicable: hide for single-day / no dates yet; explain the other reasons. Saved hours are KEPT (never
  // removed implicitly) and simply not shown while they do not apply.
  if (!app.ok) {
    const keptNote =
      savedCount > 0
        ? " The hours already saved for each day are kept, but not shown while this is the case."
        : "";
    if (
      app.reason === "single_day" ||
      app.reason === "no_start" ||
      app.reason === "no_end" ||
      app.reason === "date_tbd" ||
      app.reason === "end_before_start"
    ) {
      return savedCount > 0 ? (
        <p className="text-sm text-gray-600" data-testid="daily-hours-kept">
          {app.reason === "single_day"
            ? "This event now runs on one day."
            : app.reason === "date_tbd"
              ? "The date is now TBD."
              : "The event does not run over several days right now."}
          {keptNote}
        </p>
      ) : null;
    }
    return (
      <div
        className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600"
        data-testid="daily-hours-unavailable"
      >
        <span className="font-medium text-gray-700">Hours each day: </span>
        {DAILY_HOURS_REASON_TEXT[app.reason]}
        {keptNote}
      </div>
    );
  }

  // Saved hours not read yet (or the read failed): nothing can be edited, and a save leaves them alone.
  if (!value.initialized) {
    return (
      <div
        className={`rounded-md border p-3 text-sm ${loadError ? "border-red-200 bg-red-50 text-red-800" : "border-gray-200 bg-gray-50 text-gray-600"}`}
        data-testid={
          loadError ? "daily-hours-load-error" : "daily-hours-loading"
        }
      >
        <span className="font-medium">Hours each day: </span>
        {loadError
          ? `could not load the hours saved for each day (${loadError}). They are left unchanged when you save; reload the page to edit them.`
          : "loading the hours saved for each day..."}
      </div>
    );
  }

  const readOnly = !!readOnlyReason;
  const days = reconciled!.days;
  const savedDates = new Set((saved ?? []).map((d) => d.service_date));
  const droppedSaved = (saved ?? [])
    .map((d) => d.service_date)
    .filter((d) => !app.dates.includes(d));
  const addedVsSaved =
    savedCount > 0 ? app.dates.filter((d) => !savedDates.has(d)) : [];

  const setDay = (date: string, field: "opens_at" | "closes_at", v: string) => {
    const next = days.map((d) =>
      d.service_date === date ? { ...d, [field]: v } : d,
    );
    onChange({ ...value, days: next, touched: true });
  };

  return (
    <div
      className="rounded-md border border-gray-200 p-4"
      data-testid="daily-hours-editor"
    >
      <div className="flex items-start gap-2">
        <input
          id="dailyHoursEnabled"
          type="checkbox"
          className="mt-0.5 h-4 w-4 cursor-pointer rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:cursor-not-allowed"
          checked={value.enabled}
          disabled={readOnly}
          onChange={(e) =>
            onChange({
              ...value,
              enabled: e.target.checked,
              days:
                e.target.checked && value.days.length === 0 ? days : value.days,
              touched: true,
            })
          }
        />
        <label htmlFor="dailyHoursEnabled" className="cursor-pointer text-sm">
          <span className="font-medium text-gray-900">
            Set hours for each day ({app.dates.length} days)
          </span>
          <span className="block text-gray-500">
            The event is saved as one time range: {formatClock(app.startClock)}{" "}
            on {formatDayLabel(app.dates[0])} to {formatClock(app.endClock)} on{" "}
            {formatDayLabel(app.dates[app.dates.length - 1])}. If the hours
            differ by day (for example open until 5 PM on Saturday but 3 PM on
            Sunday), set them here so member services and staff see the real
            hours for every day. Times are in the event's time zone.
          </span>
        </label>
      </div>

      {readOnly && (
        <p
          className="mt-2 text-sm text-gray-600"
          data-testid="daily-hours-readonly"
        >
          {readOnlyReason}
        </p>
      )}

      {value.enabled &&
        savedCount > 0 &&
        (droppedSaved.length > 0 || addedVsSaved.length > 0) && (
          <p
            className="mt-3 rounded bg-amber-50 p-2 text-sm text-amber-800"
            data-testid="daily-hours-dates-changed"
          >
            The event's dates changed.
            {droppedSaved.length > 0 &&
              ` Hours for ${droppedSaved.map(formatDayLabel).join(", ")} will be removed.`}
            {addedVsSaved.length > 0 &&
              ` ${addedVsSaved.map(formatDayLabel).join(", ")} ${addedVsSaved.length === 1 ? "was" : "were"} added with the starting hours below.`}{" "}
            Check them before saving.
          </p>
        )}

      {!value.enabled && value.touched && savedCount > 0 && !readOnly && (
        <p
          className="mt-3 text-sm text-amber-700"
          data-testid="daily-hours-will-clear"
        >
          Saving will remove the hours set for each day; staff will see the
          single time range above.
        </p>
      )}

      {value.enabled && (
        <table className="mt-3 w-full max-w-lg text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
              <th className="py-1 pr-3 font-medium">Day</th>
              <th className="py-1 pr-3 font-medium">Opens</th>
              <th className="py-1 font-medium">Closes</th>
            </tr>
          </thead>
          <tbody>
            {days.map((d, i) => {
              const first = i === 0;
              const last = i === days.length - 1;
              const err = errors[d.service_date];
              return (
                <tr
                  key={d.service_date}
                  className="align-top"
                  data-testid={`daily-hours-row-${d.service_date}`}
                >
                  <td className="py-1 pr-3 whitespace-nowrap font-medium text-gray-800">
                    {formatDayLabel(d.service_date)}
                  </td>
                  <td className="py-1 pr-3">
                    {first ? (
                      <span
                        className="text-gray-700"
                        title="Day 1 opens at the event's start time. Change it above."
                      >
                        {formatClock(d.opens_at)}
                        <span className="block text-xs text-gray-500">
                          event start
                        </span>
                      </span>
                    ) : (
                      <select
                        aria-label={`${formatDayLabel(d.service_date)} opens`}
                        value={d.opens_at}
                        disabled={readOnly}
                        onChange={(e) =>
                          setDay(d.service_date, "opens_at", e.target.value)
                        }
                        className={`rounded border px-2 py-1 disabled:bg-gray-100 ${err ? "border-red-400" : "border-gray-300"}`}
                      >
                        <option value="">Select...</option>
                        {timeSelectOptions(d.opens_at || undefined).map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td className="py-1">
                    {last ? (
                      <span
                        className="text-gray-700"
                        title="The last day closes at the event's end time. Change it above."
                      >
                        {formatClock(d.closes_at)}
                        <span className="block text-xs text-gray-500">
                          event end
                        </span>
                      </span>
                    ) : (
                      <select
                        aria-label={`${formatDayLabel(d.service_date)} closes`}
                        value={d.closes_at}
                        disabled={readOnly}
                        onChange={(e) =>
                          setDay(d.service_date, "closes_at", e.target.value)
                        }
                        className={`rounded border px-2 py-1 disabled:bg-gray-100 ${err ? "border-red-400" : "border-gray-300"}`}
                      >
                        <option value="">Select...</option>
                        {timeSelectOptions(d.closes_at || undefined).map(
                          (t) => (
                            <option key={t.value} value={t.value}>
                              {t.label}
                            </option>
                          ),
                        )}
                      </select>
                    )}
                    {err && (
                      <span className="mt-0.5 block text-xs text-red-600">
                        {err}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
